import React, { useState } from 'react';
import { 
  Users, GraduationCap, ClipboardCheck, FileText, 
  BookOpen, Star, MapPin, CalendarDays, Megaphone,
  ScrollText, MessageCircle, XCircle, Clock, TrendingUp, Info,
  AlertCircle, Shield, CheckCircle2
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useData } from '../context/DataContext';
import { useLoading } from '../hooks/useLoading';
import { Topbar } from '../components/Topbar';
import { Card, Button, Badge, Skeleton } from '../components/ui';
import { motion, AnimatePresence } from 'framer-motion';

export function Dashboard() {
  const { user } = useAuth();
  const { notices, studentEnrollments, calculateFrequency } = useData();
  const loading = useLoading(600);

  if (!user) return null;

  const isEnrolled = studentEnrollments && studentEnrollments.length > 0;
  const currentEnrollment = isEnrolled ? studentEnrollments[0] : null;
  const frequency = isEnrolled ? calculateFrequency(currentEnrollment.enrollmentId, currentEnrollment.course, currentEnrollment.turma) : 0;

  const stats = [
    ['Status de Matrícula', isEnrolled ? 'Ativa' : 'Não Iniciada', isEnrolled ? `Matrícula: ${currentEnrollment?.enrollmentId}` : 'Aguardando seleção de curso', GraduationCap, isEnrolled ? 'green' : 'yellow'],
    ['Curso Selecionado',
      isEnrolled
        ? (Array.isArray(currentEnrollment?.courses) && currentEnrollment.courses.length > 1
            ? `${currentEnrollment.courses.length} cursos`
            : (currentEnrollment?.course || currentEnrollment?.courseName))
        : 'Nenhum',
      isEnrolled
        ? (Array.isArray(currentEnrollment?.courses) && currentEnrollment.courses.length > 1
            ? currentEnrollment.courses.join(' · ')
            : `Turma ${currentEnrollment?.turma}`)
        : 'Selecione no catálogo',
      BookOpen, isEnrolled ? 'purple' : 'muted'],
    ['Frequência Geral', isEnrolled ? `${frequency}%` : '--', isEnrolled ? (frequency >= 75 ? 'Excelente desempenho' : 'Atenção necessária') : 'Sem dados', TrendingUp, isEnrolled ? (frequency >= 75 ? 'green' : 'red') : 'muted'],
    ['Próximos Passos', isEnrolled ? 'Aguardar Início' : 'Escolher Curso', isEnrolled ? 'Consulte o Discord' : 'Acesse o catálogo', ClipboardCheck, 'purple'],
  ];

  const filteredNotices = (notices || []).filter(n => {
    if (user.type === 'professor') {
      return n.type === 'professor' || n.authorId === (user.enrollmentId || user.email);
    }
    if (user.type === 'student') {
      const studentCourses = studentEnrollments.flatMap(e =>
        Array.isArray(e.courses) && e.courses.length > 0
          ? e.courses.map(c => c.toLowerCase())
          : [(e.course || e.courseName || '').toLowerCase()].filter(Boolean)
      );
      if (!n.targetCourse || n.targetCourse === 'all') return n.type === 'student';
      return studentCourses.includes(n.targetCourse.toLowerCase());
    }
    return n.type === user.type;
  }).slice(0, 3);

  const userName = isEnrolled ? (currentEnrollment?.fullName || user.name) : (user?.name || 'Estudante');

  return (
    <div className="dashboardWrapper">
      <Topbar />
      
      <section className="welcomeBanner bg-gradient-to-r from-purple/20 to-transparent p-10 rounded-[2.5rem] border border-purple/20 mb-8 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-purple/10 blur-[80px] rounded-full -mr-32 -mt-32" />
        
        <div className="flex flex-col md:flex-row gap-8 items-center relative z-10">
          <div className="w-24 h-24 rounded-3xl border-4 border-[#1d2a42] bg-[#0a1120] flex items-center justify-center shrink-0 shadow-2xl">
            <div className="w-full h-full flex items-center justify-center text-4xl font-black text-white bg-gradient-to-br from-purple/20 to-purple/5">
              {userName[0]}
            </div>
          </div>
          
          <div className="flex-1 text-center md:text-left">
            <h2 className="text-5xl font-black mb-3 tracking-tighter">Olá, {userName.split(' ')[0]}! 👋</h2>
            <p className="text-xl text-muted max-w-2xl mb-8 font-medium">
              Bem-vindo ao seu Portal Acadêmico. Acompanhe seu progresso, notas e comunicados oficiais.
            </p>
            <div className="flex flex-wrap gap-4 justify-center md:justify-start">
              {!isEnrolled ? (
                <Button variant="outline" className="flex items-center gap-2 py-4 px-8 rounded-2xl font-black text-sm uppercase tracking-widest" onClick={() => window.location.href = '/cursos'}>
                  <BookOpen size={18} /> Escolher meu Curso
                </Button>
              ) : (
                <Button className="flex items-center gap-2 py-4 px-8 rounded-2xl font-black text-sm uppercase tracking-widest bg-purple hover:bg-purple/80 border-none shadow-xl shadow-purple/20" onClick={() => window.location.href = '/minhas-aulas'}>
                  <GraduationCap size={18} /> Minhas Aulas
                </Button>
              )}
              <Button className="flex items-center gap-2 py-4 px-8 rounded-2xl font-black text-sm uppercase tracking-widest bg-[#5865F2] hover:bg-[#4752C4] border-none shadow-xl shadow-[#5865F2]/20" onClick={() => window.open('https://discord.gg/lumenfall', '_blank')}>
                <MessageCircle size={18} /> Discord Oficial
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="stats">
        {stats.map(([title, value, sub, Icon, color], idx) => (
          <article className={`stat ${color}`} key={idx}>
            {loading ? (
              <div className="w-full">
                <Skeleton className="h-4 w-24 mb-2" />
                <Skeleton className="h-8 w-16 mb-2" />
                <Skeleton className="h-3 w-32" />
              </div>
            ) : (
              <>
                <div className="statIcon"><Icon size={30}/></div>
                <div>
                  <h3>{title}</h3>
                  <strong>{value}</strong>
                  <p>{sub}</p>
                </div>
              </>
            )}
          </article>
        ))}
      </section>

      {isEnrolled && (
        <section className="mb-8">
          <Card title="Visão Geral do Curso">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 py-4">
              <div className="bg-purple/5 rounded-3xl p-6 border border-purple/10">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-purple/20 rounded-xl text-purple">
                    <BookOpen size={20} />
                  </div>
                  <h4 className="font-bold">Seu Curso</h4>
                </div>
                <p className="text-2xl font-black text-white mb-1">{currentEnrollment?.course || currentEnrollment?.courseName}</p>
                <p className="text-sm text-muted">Turma {currentEnrollment?.turma}</p>
              </div>

              <div className="bg-green/5 rounded-3xl p-6 border border-green/10">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-green/20 rounded-xl text-green">
                    <TrendingUp size={20} />
                  </div>
                  <h4 className="font-bold">Frequência</h4>
                </div>
                <p className="text-2xl font-black text-white mb-1">{frequency}%</p>
                <p className="text-sm text-muted">{frequency >= 75 ? 'Acima da média (75%)' : 'Abaixo da média (75%)'}</p>
              </div>

              <div className="bg-[#5865F2]/5 rounded-3xl p-6 border border-[#5865F2]/10">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-[#5865F2]/20 rounded-xl text-[#5865F2]">
                    <MessageCircle size={20} />
                  </div>
                  <h4 className="font-bold">Comunicação</h4>
                </div>
                <p className="text-sm text-muted mb-4">Horários e avisos são postados no Discord.</p>
                <Button variant="outline" size="sm" className="w-full border-[#5865F2]/30 text-[#5865F2] hover:bg-[#5865F2]/10" onClick={() => window.open('https://discord.gg/lumenfall', '_blank')}>Ver no Discord</Button>
              </div>
            </div>
          </Card>
        </section>
      )}

      <section className="bottomGrid">
        <div className="flex flex-col gap-6">
          <NoticesPanel notices={filteredNotices} loading={loading} />
          <QuickActions loading={loading} />
        </div>
        <div className="flex flex-col gap-6">
          <AcademicCalendarPanel loading={loading} />
          <Card title="Precisa de Ajuda?">
            <p className="text-sm text-muted mb-4">Nossa equipe de suporte está pronta para te auxiliar em cada etapa do seu ingresso.</p>
            <div className="flex flex-col gap-2">
              <Button variant="outline" className="w-full justify-start gap-2">
                <MessageCircle size={18} /> Central de Atendimento
              </Button>
              <Button variant="outline" className="w-full justify-start gap-2">
                <FileText size={18} /> FAQ de Matrícula
              </Button>
            </div>
          </Card>
        </div>
      </section>
    </div>
  );
}

function AcademicCalendarPanel({ loading }) {
  return (
    <Card title="Calendário Acadêmico">
      <div className="mt-4 flex flex-col items-center text-center">
        <div className="w-16 h-16 bg-purple/10 rounded-full flex items-center justify-center mb-4">
          <CalendarDays size={32} className="text-purple" />
        </div>
        <h4 className="font-bold mb-2">Cronograma Semanal</h4>
        <p className="text-sm text-muted leading-relaxed mb-6">
          Os horários e datas das aulas são atualizados semanalmente. 
          Consulte o canal <strong>#horarios</strong> no nosso Discord oficial para a programação completa.
        </p>
        <Button className="w-full flex items-center justify-center gap-2" onClick={() => window.open('https://discord.gg/lumenfall', '_blank')}>
          <MessageCircle size={18} />
          Ver no Discord
        </Button>
      </div>
    </Card>
  );
}

function NoticesPanel({ notices, loading }) {
  return (
    <Card title="Avisos e Comunicados" actions={<Button variant="outline" size="sm" onClick={() => window.location.href = '/avisos'}>Ver todos</Button>} className="notices">
      {loading ? (
        <div className="flex flex-col gap-4 mt-4">
          {[1, 2].map(i => <Skeleton key={i} className="h-14 w-full" />)}
        </div>
      ) : (
        <div className="mt-4">
          {notices.length > 0 ? (
            notices.map((n) => (
              <div className="notice" key={n.id}>
                <div className={`noticeIcon ${n.color}`}><Megaphone size={20}/></div>
                <div><strong>{n.title}</strong><span className="text-xs text-muted line-clamp-1">{n.sub}</span></div>
                <small className="text-xs opacity-50">{n.time}</small>
              </div>
            ))
          ) : (
            <p className="text-sm text-muted text-center py-4">Nenhum aviso no momento.</p>
          )}
        </div>
      )}
    </Card>
  );
}

function QuickActions({ loading }) {
  return (
    <Card title="Ações Rápidas">
      <div className="grid grid-cols-2 gap-3 mt-4">
        <Button variant="outline" className="flex flex-col gap-2 h-auto py-4 rounded-2xl border-white/5 bg-white/[0.02] hover:bg-white/[0.05]" onClick={() => window.location.href = '/notas'}>
          <ScrollText className="text-purple" size={20}/>
          <span className="text-xs font-bold uppercase tracking-wider">Boletim</span>
        </Button>
        <Button variant="outline" className="flex flex-col gap-2 h-auto py-4 rounded-2xl border-white/5 bg-white/[0.02] hover:bg-white/[0.05]" onClick={() => window.location.href = '/frequencia'}>
          <TrendingUp className="text-purple" size={20}/>
          <span className="text-xs font-bold uppercase tracking-wider">Frequência</span>
        </Button>
        <Button variant="outline" className="flex flex-col gap-2 h-auto py-4 rounded-2xl border-white/5 bg-white/[0.02] hover:bg-white/[0.05]" onClick={() => window.location.href = '/matricula'}>
          <FileText className="text-purple" size={20}/>
          <span className="text-xs font-bold uppercase tracking-wider">Matrícula</span>
        </Button>
        <Button variant="outline" className="flex flex-col gap-2 h-auto py-4 rounded-2xl border-white/5 bg-white/[0.02] hover:bg-white/[0.05]" onClick={() => window.location.href = '/configuracoes'}>
          <Users className="text-purple" size={20}/>
          <span className="text-xs font-bold uppercase tracking-wider">Perfil</span>
        </Button>
      </div>
    </Card>
  );
}
